// SurfaceVistas — one clean low-poly silhouette per inhabited chapter.
// Kept deliberately sparse: a thin plate + a few readable shapes.
// Only the active scene's vista is drawn, so the planet never wears
// four city kits at once.

import { useMemo, useRef, type ReactNode } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { SCENES } from './planetSections';
import { projectToPlanetSurface } from './Planet';

type VistaKind = 'paris' | 'tokyo' | 'kuala-lumpur' | 'sydney';

interface VistaSpec {
  id: string;
  sceneId: number;
  kind: VistaKind;
  position: [number, number, number];
  scale: number;
  yaw: number;
  label: string;
  description: string;
}

/** Scene ids that own a surface vista (and therefore hide their landmark). */
export const VISTA_SCENE_IDS: ReadonlySet<number> = new Set([1, 2, 3, 4]);

const VISTAS: VistaSpec[] = [
  {
    id: 'paris-tower',
    sceneId: 1,
    kind: 'paris',
    position: [2.18, 0.52, 1.02],
    scale: 0.5,
    yaw: -0.35,
    label: 'Paris signal',
    description: 'A warm tower signal on the first inhabited edge of the world.',
  },
  {
    id: 'tokyo-fuji',
    sceneId: 2,
    kind: 'tokyo',
    position: [-1.42, 1.36, 2.08],
    scale: 0.52,
    yaw: 0.45,
    label: 'Tokyo ridge',
    description: 'A compressed skyline and a quiet mountain line.',
  },
  {
    id: 'kuala-lumpur-towers',
    sceneId: 3,
    kind: 'kuala-lumpur',
    position: [0.9, -0.9, 2.48],
    scale: 0.54,
    yaw: -0.1,
    label: 'Kuala Lumpur relay',
    description: 'Two towers carry the projects chapter back into orbit.',
  },
  {
    id: 'sydney-harbor',
    sceneId: 4,
    kind: 'sydney',
    position: [-1.86, 1.9, -0.92],
    scale: 0.5,
    yaw: 0.2,
    label: 'Sydney harbor',
    description: 'A small harbor for fragments, field notes, and unfinished ideas.',
  },
];

const LOCAL_UP = new THREE.Vector3(0, 1, 0);
const INK = '#F5EFEA';
const ACCENT = '#01847F';
const DEEP = '#122E8A';
const WARM = '#F9D2E4';

/** A faceted terrace whose outer skirt drops with the planet's curvature.
 *  A flat cylinder touches a sphere only at its center and visibly floats at
 *  the edges; this open-bottom mesh meets the terrain around its perimeter. */
const TERRAIN_FOOTING_GEOMETRY = (() => {
  const segments = 12;
  const topRadius = 0.48;
  const groundRadius = 0.56;
  const topY = 0.034;
  const groundY = -0.105;
  const positions: number[] = [0, topY, 0];
  const indices: number[] = [];

  for (let i = 0; i < segments; i++) {
    const angle = (i / segments) * Math.PI * 2;
    positions.push(Math.cos(angle) * topRadius, topY, Math.sin(angle) * topRadius);
  }
  for (let i = 0; i < segments; i++) {
    const angle = (i / segments) * Math.PI * 2;
    positions.push(
      Math.cos(angle) * groundRadius,
      groundY,
      Math.sin(angle) * groundRadius,
    );
  }

  for (let i = 0; i < segments; i++) {
    const next = (i + 1) % segments;
    const topA = 1 + i;
    const topB = 1 + next;
    const groundA = 1 + segments + i;
    const groundB = 1 + segments + next;
    indices.push(0, topB, topA);
    indices.push(topA, topB, groundB, topA, groundB, groundA);
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return geometry;
})();

export interface SurfaceVistasProps {
  activeSceneIndex: number;
}

function useSurfaceTransform(position: [number, number, number], yaw: number) {
  return useMemo(() => {
    const outward = new THREE.Vector3(...position).normalize();
    const quat = new THREE.Quaternion().setFromUnitVectors(LOCAL_UP, outward);
    const yawQuat = new THREE.Quaternion().setFromAxisAngle(LOCAL_UP, yaw);
    quat.multiply(yawQuat);
    const euler = new THREE.Euler().setFromQuaternion(quat);
    return {
      rotation: [euler.x, euler.y, euler.z] as [number, number, number],
    };
  }, [position, yaw]);
}

function VistaShell({
  spec,
  children,
}: {
  spec: VistaSpec;
  children: ReactNode;
}) {
  const { rotation } = useSurfaceTransform(spec.position, spec.yaw);
  const foundationColor = SCENES[spec.sceneId]?.planetTint ?? DEEP;
  const shellRef = useRef<THREE.Group | null>(null);
  const surfacePosition = useMemo(
    () =>
      projectToPlanetSurface(
        spec.position,
        SCENES[spec.sceneId]?.planetRotation ?? 0,
        0.02,
      ),
    [spec.position, spec.sceneId],
  );

  useFrame((state, delta) => {
    const shell = shellRef.current;
    if (!shell) return;
    const planetRotation =
      state.scene.getObjectByName('planet')?.rotation.y ??
      SCENES[spec.sceneId]?.planetRotation ??
      0;
    const point = projectToPlanetSurface(spec.position, planetRotation, 0.02);
    shell.position.set(point[0], point[1], point[2]);
    const damp = 1 - Math.exp(-delta * 5);
    shell.scale.setScalar(THREE.MathUtils.lerp(shell.scale.x, spec.scale, damp));
  });

  const openVista = (event: { stopPropagation: () => void }) => {
    if (typeof window === 'undefined' || window.__planetMode !== 'exploration') return;
    event.stopPropagation();
    const point = new THREE.Vector3();
    shellRef.current?.getWorldPosition(point);
    window.__planetShowInfoCard?.({
      kind: 'vista',
      id: spec.id,
      title: spec.label,
      blurb: spec.description,
      worldPos: [point.x, point.y, point.z],
    });
  };

  return (
    <group
      ref={shellRef}
      name={`surface-vista-${spec.id}`}
      position={surfacePosition}
      rotation={rotation}
      scale={spec.scale * 0.92}
      raycast={() => null}
      userData={{ surfaceVista: spec.id }}
    >
      {/* Two-stage terrain footing: the lower polygon follows the planet,
          while the smaller upper terrace gives the architecture a clean datum. */}
      <mesh
        name={`${spec.id}-foundation`}
        geometry={TERRAIN_FOOTING_GEOMETRY}
        onClick={openVista}
        onPointerOver={() => {
          if (typeof window !== 'undefined' && window.__planetMode === 'exploration') {
            document.body.style.cursor = 'pointer';
          }
        }}
        onPointerOut={() => {
          document.body.style.cursor = '';
        }}
      >
        <meshStandardMaterial
          color={foundationColor}
          roughness={0.92}
          metalness={0.01}
          flatShading
        />
      </mesh>
      <mesh name={`${spec.id}-terrace`} position={[0, 0.05, 0]} raycast={() => null}>
        <cylinderGeometry args={[0.43, 0.48, 0.032, 12]} />
        <meshStandardMaterial
          color={INK}
          roughness={0.82}
          metalness={0.01}
          flatShading
        />
      </mesh>
      <group position={[0, 0.07, 0]}>{children}</group>
    </group>
  );
}

function StructuralBeam({
  from,
  to,
  radius = 0.014,
  color = INK,
}: {
  from: [number, number, number];
  to: [number, number, number];
  radius?: number;
  color?: string;
}) {
  const transform = useMemo(() => {
    const start = new THREE.Vector3(...from);
    const end = new THREE.Vector3(...to);
    const direction = end.clone().sub(start);
    const length = direction.length();
    const midpoint = start.clone().add(end).multiplyScalar(0.5);
    const quaternion = new THREE.Quaternion().setFromUnitVectors(
      LOCAL_UP,
      direction.normalize(),
    );
    return { length, midpoint, quaternion };
  }, [from, to]);

  return (
    <mesh
      position={transform.midpoint}
      quaternion={transform.quaternion}
      raycast={() => null}
    >
      <cylinderGeometry args={[radius, radius, transform.length, 5]} />
      <meshStandardMaterial
        color={color}
        emissive={color}
        emissiveIntensity={0.08}
        roughness={0.68}
        flatShading
      />
    </mesh>
  );
}

/** About — an open, faceted lattice tower that holds up under the close lens. */
function ParisTower() {
  const flagRef = useRef<THREE.Mesh | null>(null);
  const FLAG_X = [0.09, 0.13, 0.08, 0.12] as const;

  useFrame((state) => {
    const flag = flagRef.current;
    if (!flag) return;
    const reduced =
      typeof window !== 'undefined' && window.__planetReducedMotion === true;
    if (reduced) {
      flag.scale.x = 1;
      return;
    }
    const frame = Math.floor(state.clock.elapsedTime * 8);
    flag.scale.x = FLAG_X[frame % FLAG_X.length] / 0.1;
  });

  return (
    <group name="vista-paris-tower">
      {[
        [[-0.2, 0.02, -0.12], [-0.04, 0.72, -0.025]],
        [[0.2, 0.02, -0.12], [0.04, 0.72, -0.025]],
        [[-0.2, 0.02, 0.12], [-0.04, 0.72, 0.025]],
        [[0.2, 0.02, 0.12], [0.04, 0.72, 0.025]],
      ].map(([from, to], index) => (
        <StructuralBeam
          key={`leg-${index}`}
          from={from as [number, number, number]}
          to={to as [number, number, number]}
          radius={0.018}
        />
      ))}
      {[
        [[-0.15, 0.22, 0], [0.15, 0.22, 0]],
        [[-0.11, 0.43, 0], [0.11, 0.43, 0]],
        [[-0.07, 0.63, 0], [0.07, 0.63, 0]],
        [[-0.15, 0.22, 0], [0.1, 0.43, 0]],
        [[0.15, 0.22, 0], [-0.1, 0.43, 0]],
        [[-0.1, 0.43, 0], [0.06, 0.63, 0]],
        [[0.1, 0.43, 0], [-0.06, 0.63, 0]],
      ].map(([from, to], index) => (
        <StructuralBeam
          key={`brace-${index}`}
          from={from as [number, number, number]}
          to={to as [number, number, number]}
          radius={0.01}
          color={index < 3 ? WARM : ACCENT}
        />
      ))}
      {[0.22, 0.43, 0.68].map((y, index) => (
        <mesh key={y} position={[0, y, 0]} raycast={() => null}>
          <boxGeometry args={[0.32 - index * 0.085, 0.025, 0.16 - index * 0.035]} />
          <meshStandardMaterial color={WARM} roughness={0.65} flatShading />
        </mesh>
      ))}
      <StructuralBeam from={[0, 0.68, 0]} to={[0, 1.16, 0]} radius={0.016} color={ACCENT} />
      <mesh ref={flagRef} position={[0.06, 1.14, 0]} raycast={() => null}>
        <boxGeometry args={[0.1, 0.035, 0.01]} />
        <meshBasicMaterial color={WARM} />
      </mesh>
    </group>
  );
}

/** Skills — a compressed skyline, layered ridge, and a quiet river. */
function TokyoFuji() {
  const mountainRef = useRef<THREE.Group | null>(null);
  const PEAK_Y = [1, 1.04, 0.98, 1.02] as const;
  const riverCurve = useMemo(
    () =>
      new THREE.CatmullRomCurve3([
        new THREE.Vector3(-0.42, 0.035, 0.2),
        new THREE.Vector3(-0.2, 0.04, 0.28),
        new THREE.Vector3(0.02, 0.038, 0.19),
        new THREE.Vector3(0.24, 0.04, 0.25),
        new THREE.Vector3(0.48, 0.035, 0.16),
      ]),
    [],
  );

  useFrame((state) => {
    const mountain = mountainRef.current;
    if (!mountain) return;
    const reduced =
      typeof window !== 'undefined' && window.__planetReducedMotion === true;
    if (reduced) {
      mountain.scale.y = 1;
      return;
    }
    const frame = Math.floor(state.clock.elapsedTime * 6);
    mountain.scale.y = PEAK_Y[frame % PEAK_Y.length];
  });

  return (
    <group name="vista-tokyo-fuji">
      {[
        { x: -0.22, h: 0.42, w: 0.12 },
        { x: -0.06, h: 0.58, w: 0.14 },
        { x: 0.12, h: 0.36, w: 0.11 },
      ].map((b) => (
        <mesh
          key={b.x}
          position={[b.x, b.h / 2 + 0.02, 0.06]}
          raycast={() => null}
        >
          <boxGeometry args={[b.w, b.h, 0.12]} />
          <meshStandardMaterial color={INK} roughness={0.78} flatShading />
        </mesh>
      ))}
      <group ref={mountainRef} position={[0.2, 0.02, -0.14]}>
        {[
          { x: -0.3, z: -0.04, r: 0.18, h: 0.3, color: '#25499a' },
          { x: -0.08, z: -0.08, r: 0.28, h: 0.52, color: DEEP },
          { x: 0.17, z: -0.02, r: 0.22, h: 0.4, color: '#193b86' },
          { x: 0.37, z: -0.12, r: 0.15, h: 0.27, color: '#3157a1' },
        ].map((peak) => (
          <mesh
            key={peak.x}
            position={[peak.x, peak.h / 2, peak.z]}
            raycast={() => null}
          >
            <coneGeometry args={[peak.r, peak.h, 5]} />
            <meshStandardMaterial
              color={peak.color}
              roughness={0.9}
              flatShading
            />
          </mesh>
        ))}
        <mesh position={[-0.08, 0.48, -0.08]} raycast={() => null}>
          <coneGeometry args={[0.09, 0.12, 5]} />
          <meshStandardMaterial color={INK} roughness={0.7} flatShading />
        </mesh>
      </group>
      <mesh raycast={() => null}>
        <tubeGeometry args={[riverCurve, 18, 0.018, 5, false]} />
        <meshStandardMaterial
          color="#8ec5ff"
          emissive="#2f7fc0"
          emissiveIntensity={0.2}
          roughness={0.35}
        />
      </mesh>
    </group>
  );
}

/** Projects — twin towers + one skybridge. Nothing else. */
function KualaLumpurTowers() {
  return (
    <group name="vista-kuala-lumpur-towers">
      {[-0.16, 0.16].map((x) => (
        <group key={x} position={[x, 0, 0]}>
          {[
            { y: 0.2, h: 0.36, top: 0.085, bottom: 0.11 },
            { y: 0.51, h: 0.25, top: 0.065, bottom: 0.085 },
            { y: 0.73, h: 0.19, top: 0.045, bottom: 0.065 },
          ].map((tier, index) => (
            <group key={tier.y}>
              <mesh position={[0, tier.y, 0]} raycast={() => null}>
                <cylinderGeometry args={[tier.top, tier.bottom, tier.h, 8]} />
                <meshStandardMaterial color={DEEP} roughness={0.7} flatShading />
              </mesh>
              <mesh position={[0, tier.y + tier.h / 2, 0]} raycast={() => null}>
                <cylinderGeometry args={[tier.bottom * 1.08, tier.bottom * 1.08, 0.018, 8]} />
                <meshStandardMaterial color={index === 2 ? ACCENT : INK} roughness={0.55} />
              </mesh>
            </group>
          ))}
          <mesh position={[0, 0.97, 0]} raycast={() => null}>
            <coneGeometry args={[0.045, 0.34, 8]} />
            <meshStandardMaterial
              color={INK}
              emissive={INK}
              emissiveIntensity={0.18}
              roughness={0.6}
              flatShading
            />
          </mesh>
        </group>
      ))}
      <mesh position={[0, 0.6, 0]} raycast={() => null}>
        <boxGeometry args={[0.32, 0.045, 0.075]} />
        <meshStandardMaterial color={ACCENT} roughness={0.55} flatShading />
      </mesh>
    </group>
  );
}

/** Notes — three sails on a quiet quay. */
function SydneyHarbor() {
  return (
    <group name="vista-sydney-harbor">
      <mesh position={[0, 0.05, 0]} raycast={() => null}>
        <boxGeometry args={[0.72, 0.06, 0.2]} />
        <meshStandardMaterial color={DEEP} roughness={0.85} flatShading />
      </mesh>
      {[
        { x: -0.2, rot: -0.55, s: 0.95 },
        { x: 0.0, rot: -0.42, s: 1.1 },
        { x: 0.2, rot: -0.3, s: 0.88 },
      ].map((sail) => (
        <mesh
          key={sail.x}
          position={[sail.x, 0.28, 0.02]}
          rotation={[0, 0, sail.rot]}
          scale={[0.7, sail.s, 0.28]}
          raycast={() => null}
        >
          <coneGeometry args={[0.14, 0.48, 3]} />
          <meshStandardMaterial
            color={WARM}
            emissive={WARM}
            emissiveIntensity={0.16}
            roughness={0.62}
            flatShading
          />
        </mesh>
      ))}
      <mesh position={[0, 0.08, 0.22]} rotation={[Math.PI / 2, 0, 0]} raycast={() => null}>
        <torusGeometry args={[0.36, 0.01, 4, 48, Math.PI]} />
        <meshBasicMaterial color={ACCENT} transparent opacity={0.45} />
      </mesh>
    </group>
  );
}

function renderVista(kind: VistaKind) {
  switch (kind) {
    case 'paris':
      return <ParisTower />;
    case 'tokyo':
      return <TokyoFuji />;
    case 'kuala-lumpur':
      return <KualaLumpurTowers />;
    case 'sydney':
      return <SydneyHarbor />;
    default:
      return null;
  }
}

export function SurfaceVistas({ activeSceneIndex }: SurfaceVistasProps) {
  const active = VISTAS.find((v) => v.sceneId === activeSceneIndex);
  if (!active) return null;

  return (
    <group name="surface-vistas" userData={{ pickable: false }}>
      <VistaShell key={active.id} spec={active}>
        {renderVista(active.kind)}
      </VistaShell>
    </group>
  );
}

export default SurfaceVistas;
